<script lang="ts">
    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import Button from './forms/Button.svelte';

    export let open   = false;
    export let title  = '';
    export let size: 'sm' | 'md' | 'lg' = 'md';

    const dispatch = createEventDispatcher<{ close: void }>();

    function close() {
        open = false;
        dispatch('close');
    }

    function onBackdrop(e: MouseEvent) {
        if (e.target === e.currentTarget) close();
    }

    function onKeydown(e: KeyboardEvent) {
        if (e.key === 'Escape' && open) close();
    }

    // Prevent body scroll while open
    $: if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : '';
    }

    onDestroy(() => {
        if (typeof document !== 'undefined') document.body.style.overflow = '';
    });
</script>

<svelte:window on:keydown={onKeydown} />

{#if open}
<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="modal-backdrop" on:click={onBackdrop} aria-modal="true" role="dialog">
    <div class="modal modal--{size}">
        <div class="modal-header">
            <h2 class="modal-title">{title}</h2>
            <Button variant="close" on:click={close} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
            </Button>
        </div>

        <div class="modal-body">
            <slot />
        </div>

        {#if $$slots.footer}
        <div class="modal-footer">
            <slot name="footer" />
        </div>
        {/if}
    </div>
</div>
{/if}

<style lang="scss">
    .modal-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1000;
        background: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        animation: backdrop-in 0.15s ease;
    }

    @keyframes backdrop-in {
        from { opacity: 0; }
        to   { opacity: 1; }
    }

    .modal {
        background: $color-bg-secondary;
        border: none;
        border-radius: 16px; // Larger radius for modern feel
        display: flex;
        flex-direction: column;
        max-height: calc(100vh - 2rem);
        width: 100%;
        animation: modal-in 0.25s cubic-bezier(0.4, 0, 0.2, 1);

        &--sm { max-width: 400px; }
        &--md { max-width: 560px; }
        &--lg { max-width: 800px; }
    }

    @keyframes modal-in {
        from { opacity: 0; transform: scale(0.96) translateY(10px); }
        to   { opacity: 1; transform: scale(1)    translateY(0); }
    }

    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.25rem 1.5rem 0.75rem; // Match card title padding
        gap: 1rem;
        border-bottom: 1px solid rgba($color-border, 0.5); // Subtle divider
    }

    .modal-title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 500;
        color: $color-primary;
        font-variation-settings: "wdth" 100, "wght" 500, "opsz" 24;
    }

    .modal-body {
        padding: 1.5rem; // Standard padding now that divider is present
        overflow-y: auto;
        flex: 1;
        font-size: 0.9375rem;
        color: $color-text-secondary;
        line-height: 1.6;
    }

    .modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        background-color: transparent;
        border-top: none;
        border-radius: 0 0 $border-radius-large $border-radius-large;
    }
</style>
